import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { DateTime } from 'luxon';
import { Product } from '../../_cms/products/entities/Product.js';
import { ZipCity } from '../../adresse.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { RAGFundingInformation } from '../entities/RAGFundingInformation.js';

@Entity()
export class FundingCreationInformation extends AdornisEntity {
  public static override _class = 'FundingCreationInformation';

  @Field(() => [String])
  public kpfIDs: Maybe<string[]>;

  @Field(() => ZipCity)
  public city: Maybe<ZipCity>;

  @Field(() => DateTime)
  public fundingBegin: Maybe<DateTime>;

  @Field(() => DateTime)
  public fundingEnd: Maybe<DateTime>;

  @Field(() => Product)
  public productToFund: Maybe<Product>;

  @Field(() => String)
  public partnerFundingID: Maybe<string>;

  /**
   * 0 insert should, 1 insert is, 2 insert both
   */
  @Field(() => Int, {
    default: v => v ?? 2,
  })
  public creationType: Maybe<number>;
}

async function getKPFsForFundingInformation(
  fundingCreationInformation: FundingCreationInformation,
): Promise<PartialEntityData<KPF>[]> {
  const kpfCollection = await getCollection<KPF>(KPF._class);
  const selector = {};

  // If the override kpf filter is active we only want to filter the actual ids
  if (fundingCreationInformation.kpfIDs?.length) {
    selector['_id'] = { $in: fundingCreationInformation.kpfIDs };
  } else {
    // Resolve to partner city to get the location of the kpf
    if (fundingCreationInformation.city?.city) {
      const partnerCollection = await getCollection<Partner>(Partner._class);
      const partnersInCity = await partnerCollection
        .find({ 'address.zipCity.city': fundingCreationInformation.city.city })
        .toArray();
      selector['schoolID'] = { $in: partnersInCity.map(p => p._id) };
    }

    // filter for selected product
    if (fundingCreationInformation.productToFund) {
      selector['produkt._class'] = fundingCreationInformation.productToFund._class;
    }

    selector['$and'] = [];
    selector['$and'].push(
      {
        $or: [{ endDatum: { $gte: new Date() } }, { endDatum: { $exists: false } }, { endDatum: null }],
      },
      { startDatum: { $lte: new Date() } },
    );
  }

  // get kpfs
  let kpfs = await kpfCollection.find(selector).toArray();

  // filter out allready active funding from the selected partner
  kpfs = kpfs.filter(
    kpf =>
      !kpf.ragPartnerInformation?.some(
        fundingInformation =>
          fundingInformation.active &&
          !(
            [1, 2].includes(fundingCreationInformation.creationType ?? -1) &&
            fundingInformation.ragPartnerShouldID === fundingCreationInformation.partnerFundingID
          ) &&
          !(
            [0, 2].includes(fundingCreationInformation.creationType ?? -1) &&
            fundingInformation.ragPartnerIsID === fundingCreationInformation.partnerFundingID
          ),
      ),
  );

  return kpfs;
}

async function createFundings(fundingCreationInformation: FundingCreationInformation): Promise<number> {
  const kpfs = await getKPFsForFundingInformation(fundingCreationInformation);

  for (const kpfObject of kpfs) {
    const kpf = constructValue(kpfObject) as KPF;
    if (!kpf.ragPartnerInformation) kpf.ragPartnerInformation = [];
    kpf.ragPartnerInformation?.push(
      new RAGFundingInformation({
        ragPartnerIsID: [1, 2].includes(fundingCreationInformation.creationType ?? -1)
          ? fundingCreationInformation.partnerFundingID
          : null,
        ragPartnerShouldID: [0, 2].includes(fundingCreationInformation.creationType ?? -1)
          ? fundingCreationInformation.partnerFundingID
          : null,
        ragPartnerBeginsFunding: fundingCreationInformation.fundingBegin,
        ragPartnerFundingUntil: fundingCreationInformation.fundingEnd,
      }),
    );
    await kpf.save();
  }

  return kpfs.length;
}

export const getNumberOfPossibleFundingsForKPF = registerQuery({
  type: () => Int,
  operationName: 'getNumberOfPossibleFundingsForOptions',
  params: [{ type: () => FundingCreationInformation, name: 'fundingCreationInformation' }],
  resolve: (fundingCreationInformation: FundingCreationInformation) => async () =>
    (await getKPFsForFundingInformation(fundingCreationInformation)).length,
});

export const createFundingsForKPF = registerMutation({
  type: () => Int,
  operationName: 'createFundingsForOptions',
  params: [{ type: () => FundingCreationInformation, name: 'fundingCreationInformation' }],
  resolve: (fundingCreationInformation: FundingCreationInformation) => async () =>
    await createFundings(fundingCreationInformation),
});
